import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check completely.
    // AuthCallback will exchange the session_id and establish the session first.
    const hasSessionId = window.location.hash?.includes('session_id=') || 
                         window.location.search?.includes('session_id=') ||
                         window.location.pathname === '/auth/callback';
    
    if (hasSessionId) {
      // Don't set loading to false yet - let AuthCallback handle it
      return;
    }

    try {
      // First try token from localStorage (most reliable for cross-origin)
      const token = localStorage.getItem('moviebot_token');
      if (token) {
        const resp = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const userData = await resp.json();
          setUser(userData);
          setLoading(false);
          return;
        } else {
          // Token invalid, remove it
          localStorage.removeItem('moviebot_token');
        }
      }

      // No token found, user is not authenticated
      setUser(null);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const error = await response.json();
          errorMessage = error.detail || error.message || 'Login failed';
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || 'Login failed';
        }
        throw new Error(String(errorMessage));
      }

      const data = await response.json();
      localStorage.setItem('moviebot_token', data.token);
      setUser({
        user_id: data.user_id,
        name: data.name,
        email: data.email,
        subscription_tier: data.subscription_tier
      });
      return data;
    } catch (error) {
      // Re-throw with a clean string message
      const message = error?.message || 'Login failed. Please check your connection.';
      throw new Error(String(message));
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });

      if (!response.ok) {
        let errorMessage = 'Registration failed';
        try {
          const error = await response.json();
          errorMessage = error.detail || error.message || 'Registration failed';
        } catch (e) {
          errorMessage = response.statusText || 'Registration failed';
        }
        throw new Error(String(errorMessage));
      }

      const data = await response.json();
      localStorage.setItem('moviebot_token', data.token);
      setUser({
        user_id: data.user_id,
        name: data.name,
        email: data.email,
        subscription_tier: 'free'
      });
      return data;
    } catch (error) {
      const message = error?.message || 'Registration failed. Please try again.';
      throw new Error(String(message));
    }
  };

  const loginWithGoogle = () => {
    // Redirect to dashboard - Emergent Auth will append #session_id=xxx
    // The AppRouter will intercept this and render AuthCallback
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    console.log('Exchanging session with backend:', API_URL);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });

      if (!response.ok) {
        setLoading(false);
        let errorMessage = 'Session exchange failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Store the JWT token in localStorage (returned from backend)
      if (data.token) {
        localStorage.setItem('moviebot_token', data.token);
      }
      
      setUser({
        user_id: data.user_id,
        name: data.name,
        email: data.email,
        picture: data.picture,
        subscription_tier: data.subscription_tier,
        onboarding_completed: data.onboarding_completed
      });
      
      // Mark loading as complete after successful session exchange
      setLoading(false);
      
      return data;
    } catch (error) {
      console.error('Exchange session error:', error);
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('moviebot_token');
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('moviebot_token');
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('moviebot_token');
      if (token) {
        const resp = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const userData = await resp.json();
          setUser(userData);
          return userData;
        }
      }
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      loginWithGoogle,
      exchangeSession,
      logout,
      updateUser,
      refreshUser,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};
